import React, { useEffect, useState } from "react";
import CustomTable from "../../Component/Table";
import { formatDate } from "../../utils/date";
import { Button, Flex, Tag } from "antd";
import {
  fetchCategory,
  fetchUsers,
  getAllSavedItinerary,
  downloadUserItineary
} from "../../Services/user_question_history";
import { useQuery, useMutation } from "@tanstack/react-query";
import { FilterContainer } from "../../Styles/filterStyle";
import FilterSelect from "../../Component/FilterSelect";
import { useSelector } from "react-redux";
import ViewModal from "./ViewModal";
import { DatePicker } from "antd";
const { RangePicker } = DatePicker;


// Blank range: cleared out below whenever a picker is emptied.
const EMPTY_RANGE = { start_date: null, end_date: null };

function UserQuestionHistory() {
  const [viewModal, setViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({})
  const [dateRange, setDateRange] = useState(EMPTY_RANGE); // trip travel dates
  const [activityRange, setActivityRange] = useState(EMPTY_RANGE); // created_at OR updated_at

  const rangeHandler = (setter) => (dates) => {
    setter(
      dates
        ? { start_date: dates[0].format("YYYY-MM-DD"), end_date: dates[1].format("YYYY-MM-DD") }
        : EMPTY_RANGE
    );
  };
  const handleDateChange = rangeHandler(setDateRange);
  const handleActivityDateChange = rangeHandler(setActivityRange);

  const handleViewClick = (record) => {
    setViewModal(true);
    setSelectedItem(record)
  };

  const columns = [
    {
      title: "Request Id",
      dataIndex: "id",
      key: "id",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Name",
      dataIndex: "user_name",
      key: "user_name",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Category Name",
      dataIndex: "category_name",
      key: "category_name",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Category Headline",
      dataIndex: "itineary_headline_text",
      key: "itineary_headline_text",
      className: "normal",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "From Location",
      dataIndex: "from_location",
      key: "from_location",
      className: "normal",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "To Location",
      dataIndex: "to_location",
      key: "to_location",
      className: "normal",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Budget",
      dataIndex: "budget",
      key: "budget",
      render: (text, record) => {
        const currency = record?.currency || "USD";
        return text ? `${currency} ${text}` : "-";
      },
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
      render: (text, record) =>
        record?.start_date ? formatDate(record.start_date) : "-",
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      render: (text, record) =>
        record?.end_date ? formatDate(record.end_date) : "-",
    },
    {
      title: "Is Basic Open AI",
      dataIndex: "is_basic_completed",
      key: "is_basic_completed",
      render: (text, record) => (
        <>
          <Tag color={record?.is_basic_completed ? "green" : "red"}>
            {record?.is_basic_completed ? "Yes" : "No"}
          </Tag>
        </>
      ),
    },
    {
      title: "Is Advance Open AI",
      dataIndex: "is_advance_completed",
      key: "is_advance_completed",
      render: (text, record) => (
        <>
          <Tag color={record?.is_advance_completed ? "green" : "red"}>
            {record?.is_advance_completed ? "Yes" : "No"}
          </Tag>
        </>
      ),
    },
    {
      title: "Is Draft",
      dataIndex: "is_draft",
      key: "is_draft",
      render: (text, record) => (
        <>
          <Tag color={record?.is_draft ? "green" : "red"}>
            {record?.is_draft ? "Yes" : "No"}
          </Tag>
        </>
      ),
    },
    {
      title: "Input Token",
      dataIndex: "total_input_token",
      key: "total_input_token",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Output Token",
      dataIndex: "total_output_token",
      key: "total_output_token",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Total Token",
      dataIndex: "total_token",
      key: "total_token",
      render: (text, record) => (text ? text : "-"),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text, record) =>
        record?.created_at ? formatDate(record.created_at) : "-",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (text, record) =>
        record?.updated_at ? formatDate(record.updated_at) : "-",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Flex gap="small">
          <Button type="link" onClick={() => handleViewClick(record)}>
            View History
          </Button>
        </Flex>
      ),
    },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filters = useSelector((state) => state.filter.filters);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, dateRange, activityRange]);

  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading } = useQuery({
    queryKey: ["all_saved_itinerary", currentPage, pageSize, filters, dateRange, activityRange],
    queryFn: () =>
      getAllSavedItinerary({
        skip,
        limit: pageSize,
        user_ids: filters.user,
        category_ids: filters.category,
        filter_start_date: dateRange.start_date,
        filter_end_date: dateRange.end_date,
        created_updated_start_date: activityRange.start_date,
        created_updated_end_date: activityRange.end_date,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
          mutationFn: async () => {
              return await downloadUserItineary({
                user_ids: filters.user,
                category_ids: filters.category,
                filter_start_date: dateRange.start_date,
                filter_end_date: dateRange.end_date,
                created_updated_start_date: activityRange.start_date,
                created_updated_end_date: activityRange.end_date,
              });
          },
      });
  

  return (
    <div>
      <Flex justify="space-between">
        <h3>Users Question History</h3>
        <Button type="primary" onClick={triggerDownload} loading={isDownloading}>Export</Button>
      </Flex>

      <FilterContainer>
        <FilterSelect label="User" fetchOptions={fetchUsers} filterKey="user" />
        <FilterSelect label="Category" fetchOptions={fetchCategory} filterKey="category" />
        <RangePicker onChange={handleDateChange} placeholder={["Travel start", "Travel end"]} allowClear />
        <RangePicker onChange={handleActivityDateChange} placeholder={["Created / Updated from", "Created / Updated to"]} style={{ width: 340 }} allowClear />
      </FilterContainer>
      <CustomTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 180px)"}
      />
      <ViewModal
        isModalVisible={viewModal}
        handleCancel={() => setViewModal(false)}
        initialValues={selectedItem}
      />
    </div>
  );
}

export default UserQuestionHistory;
